import type { SynastryParams, SynastryResponse, SynastryType } from '@/api/schema'
import { Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Layout } from '@/app/components/Layout/Layout'
import { LocationFormPage } from '@/app/components/LocationFormPage/LocationFormPage'
import { Motion } from '@/app/components/Motion/Motion'
import { Switch } from '@/app/components/Switch/Switch'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import { useNavigate } from '@/app/hooks/useNavigate'
import { api } from '@/app/utils/api'
import { toISODate, toISOTime } from '@/app/utils/date'
import { parseError } from '@/app/utils/error'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useState } from 'preact/hooks'
import { IMask, IMaskInput } from 'react-imask'

enum View {
  Form,
  Location,
}

export const SynastryForm: FunctionalComponent = () => {
  const route = useNavigate()
  const [view, setView] = useState(View.Form)
  const [userName, setUserName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [birthTime, setBirthTime] = useState('')
  const [locationId, setLocationId] = useState<number | undefined>(undefined)
  const [locationName, setLocationName] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [type, setType] = useState<SynastryType>('astro')

  const dateValid = /^\d{2}\.\d{2}\.\d{4}$/.test(birthDate)
  const timeValid = birthTime === '' || /^\d{2}:\d{2}$/.test(birthTime)
  const submitDisabled = loading || !userName.trim() || !dateValid || !timeValid

  const back = useBack()

  if (view === View.Location) {
    return (
      <Layout key='location'>
        <Layout.Content
          onBack={() => {
            setView(View.Form)
          }}
        >
          <Motion>
            <Layout.Header>
              Место рождения
            </Layout.Header>

            <Row gap={15}>
              <LocationFormPage
                onSelect={(location) => {
                  setLocationId(location.id)
                  setLocationName(location.name_ru || location.name_en)
                  setView(View.Form)
                }}
              />

              <UI.Button
                size='l'
                type='secondary'
                onClick={() => {
                  setView(View.Form)
                }}
              >
                Назад
              </UI.Button>
            </Row>
          </Motion>
        </Layout.Content>
      </Layout>
    )
  }

  return (
    <Layout key='form'>
      <Layout.Content
        onBack={back}
      >
        <Motion>
          <Layout.Header>
            Кого проверяем?
          </Layout.Header>

          <Row gap={15}>
            <Lib.Fields>
              <Switch
                value={type}
                disabled={loading}
                options={[
                  { value: 'astro', text: 'Астрология' },
                  { value: 'matrix', text: 'Матрица' },
                ]}
                onChange={setType}
              />

              <Input.Block label='Имя'>
                <Input
                  placeholder='Введи имя'
                  value={userName}
                  onInput={setUserName}
                  onChange={setUserName}
                  disabled={loading}
                />
              </Input.Block>

              <Input.Block label='Дата рождения'>
                <Input
                  type='tel'
                  placeholder='DD.MM.YYYY'
                  value={birthDate}
                  onInput={setBirthDate}
                  onChange={setBirthDate}
                  disabled={loading}
                >
                  {props => (
                    <IMaskInput
                      {...props}
                      overwrite
                      type='tel'
                      mask={Date}
                      min={new Date(1900, 0, 1)}
                      max={new Date(2050, 0, 1)}
                    />
                  )}
                </Input>
              </Input.Block>

              {type === 'astro' && (
                <>
                  <Input.Block label='Время рождения (необязательно)'>
                    <Input
                      type='tel'
                      placeholder='HH:MM'
                      value={birthTime}
                      onInput={setBirthTime}
                      onChange={setBirthTime}
                      disabled={loading}
                    >
                      {props => (
                        <IMaskInput
                          {...props}
                          overwrite
                          type='tel'
                          mask={Date}
                          pattern='H:M'
                          blocks={{
                            H: {
                              mask: IMask.MaskedRange,
                              from: 0,
                              to: 23,
                              maxLength: 2,
                            },
                            M: {
                              mask: IMask.MaskedRange,
                              from: 0,
                              to: 59,
                              maxLength: 2,
                            },
                          }}
                          format={(date: Date | null) => {
                            if (!date) return ''
                            const hours = String(date.getHours()).padStart(2, '0')
                            const minutes = String(date.getMinutes()).padStart(2, '0')
                            return `${hours}:${minutes}`
                          }}
                          parse={(value: string) => {
                            if (!value) return null
                            const [hours, minutes] = value.split(':').map(Number)
                            if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
                            const date = new Date()
                            date.setHours(hours, minutes, 0, 0)
                            return date
                          }}
                        />
                      )}
                    </Input>
                  </Input.Block>

                  <Input.Block label='Место рождения (необязательно)'>
                    <Input
                      readOnly
                      showArrow
                      value={locationName}
                      placeholder='Выбери город'
                      disabled={loading}
                      onClick={() => {
                        setView(View.Location)
                      }}
                    />
                  </Input.Block>
                </>
              )}

              <Lib.Errors errors={errors} />
            </Lib.Fields>

            <UI.Button
              size='l'
              type='primary'
              disabled={submitDisabled}
              onClick={async () => {
                try {
                  setLoading(true)
                  setErrors([])

                  const resp = await api.post<SynastryResponse>(
                    '/synastry',
                    {
                      type,
                      userName: userName.trim(),
                      birthDate: toISODate(birthDate),
                      birthTime: toISOTime(birthTime),
                      locationId,
                    } satisfies SynastryParams,
                  )

                  metrikaGoal('synastry_create')

                  route(`/${type}/${resp.data.id}`, { replace: true })
                }
                catch (e) {
                  console.error(e)
                  setErrors(parseError(e))
                }
                finally {
                  setLoading(false)
                }
              }}
            >
              Рассчитать
            </UI.Button>
          </Row>
        </Motion>
      </Layout.Content>
    </Layout>
  )
}
